<template>
  <div class="mt-6 flex w-full items-center">
    <div data-clarity-unmask="true" class="w-full">
      <!-- URL input + Generate -->
      <label for="company-website" class="sr-only">Website URL</label>
      <InputGroup
        id="company-website"
        :model-value="url"
        :invalid="hasError"
        type="text"
        name="company-website"
        placeholder="www.example.com"
        data-clarity-unmask="true"
        @update:model-value="$emit('update:url', $event)"
        @keypress.enter="$emit('generate')"
      >
        <template #leading>
          <!-- The site's favicon once we have a URL and a favicon; otherwise
               the placeholder mark, or a pulse while generating. -->
          <img v-if="isLoading === false && typedUrl !== '' && favicon" width="24" height="24" class="h-6 w-6 rounded" :src="favicon" alt="" />
          <img v-else-if="isLoading === false" width="24" height="24" class="h-6 w-6" :src="paletteIcon" alt="" />
          <span v-else class="h-6 w-6 animate-pulse rounded-full bg-gray-700"></span>
        </template>
        <template #trailing>
          <BaseButton variant="secondary" class="m-2 shrink-0" @click.prevent="$emit('generate')">
            <template v-if="isLoading || pendingGenerate">
              <SpinnerIcon class="-ml-1 h-5 w-5 text-white" />
              {{ isLoading ? 'Generating Palette' : 'Verifying…' }}
            </template>
            <template v-else>Generate Palette</template>
          </BaseButton>
        </template>
      </InputGroup>

      <SettingsToggle :expanded="settingToggle" controls="extraction-settings" @click.prevent="$emit('toggle-settings')" />

      <!-- Turnstile renders an empty div and Cloudflare injects its iframe into
           it after mount — with nothing reserved, whatever height it lands on
           shifts the whole column (and on mobile, the entire palette below it).
           That injection happens on every page load with no interaction, so
           none of it is excluded from CLS. Anchoring the widget out of flow
           makes its height irrelevant. `interaction-only` keeps it invisible
           unless a challenge is actually required; when one is, it overlays the
           block below (hence the z-index, which has to beat the `relative`
           children of the screenshot further down) rather than displacing it.
           The inner wrapper carries the positioning so Cloudflare stays free to
           restyle its own container. -->
      <div class="relative z-20 h-0">
        <div class="absolute left-0 top-0">
          <NuxtTurnstile :ref="setTurnstileRef" :model-value="token" :options="turnstileOptions" @update:model-value="$emit('update:token', $event)" />
        </div>
      </div>

      <!-- Reserved slot for the status pill. It appears, swaps message and
           disappears on timers (verification hints at 4s/25s, then token
           arrival) — all far enough from the click that the 500ms input
           exclusion doesn't apply, so the space is held open instead. 3rem
           covers the two-line messages; a longer error still grows it. -->
      <div class="flex min-h-12 items-start">
        <VerificationBadge :state="badgeState" :message="errorMsg" />
      </div>

      <ExtractionSettings
        v-if="settingToggle"
        id="extraction-settings"
        :options="options"
        :disabled="isLoading"
        @toggle="(option) => $emit('toggle-check', option)"
        @slide="(option, value) => $emit('slide', option, value)"
      />

      <!-- Unfolds when a generation starts (and folds back on the way out)
           instead of appearing at full height in a single frame. -->
      <CollapseTransition>
        <ScreenshotSampler
          v-if="screenshotChecked && !collapsed"
          :src="screenshotSrc"
          :screenshot-loaded="screenshotLoaded"
          :screenshot-animation="screenshotAnimation"
          :is-mobile="isMobile"
          @sampled="(color) => $emit('sampled', color)"
          @loaded="$emit('screenshot-loaded')"
        />
      </CollapseTransition>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import BaseButton from '~/components/atoms/BaseButton.vue'
import SettingsToggle from '~/components/atoms/SettingsToggle.vue'
import SpinnerIcon from '~/components/atoms/SpinnerIcon.vue'
import CollapseTransition from '~/components/atoms/CollapseTransition.vue'
import InputGroup from '~/components/molecules/InputGroup.vue'
import VerificationBadge from '~/components/molecules/VerificationBadge.vue'
import ExtractionSettings from '~/components/organisms/ExtractionSettings.vue'
import ScreenshotSampler from '~/components/organisms/ScreenshotSampler.vue'
import paletteIcon from '~/assets/img/palettes_icon_bw.svg'

const props = defineProps({
  url: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  pendingGenerate: { type: Boolean, default: false },
  hasError: { type: Boolean, default: false },
  hasWarning: { type: Boolean, default: false },
  hasSuccess: { type: Boolean, default: false },
  errorMsg: { type: [String, Object], default: '' },
  favicon: { type: String, default: '' },
  typedUrl: { type: String, default: '' },
  settingToggle: { type: Boolean, default: false },
  options: { type: Array, required: true },
  token: { type: String, default: null },
  turnstileOptions: { type: Object, default: () => ({}) },
  setTurnstileRef: { type: Function, default: () => {} },
  screenshotSrc: { type: String, default: '' },
  screenshotLoaded: { type: Boolean, default: false },
  screenshotAnimation: { type: Boolean, default: false },
  isMobile: { type: Boolean, default: false },
})

defineEmits([
  'update:url',
  'generate',
  'toggle-settings',
  'toggle-check',
  'slide',
  'update:token',
  'sampled',
  'screenshot-loaded',
])

const badgeState = computed(() => {
  if (props.hasError) return 'error'
  if (props.hasWarning) return 'warning'
  if (props.hasSuccess) return 'success'
  return ''
})

const screenshotChecked = computed(() => props.options.find((item) => item.id === 2)?.checked)
const collapsed = computed(() => props.isLoading === false && props.typedUrl === '')
</script>
